import React, { useEffect, useRef, useState } from 'react';

/**
 * MotionReveal — scroll-triggered fade-in-up wrapper.
 * Uses IntersectionObserver — runs once when element enters viewport.
 */
export const MotionReveal = ({ children, delay = 0, y = 16, className = '', as: Tag = 'div' }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : `translateY(${y}px)`,
        transition: `opacity 700ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform 700ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
        willChange: 'opacity, transform',
      }}
    >
      {children}
    </Tag>
  );
};

/**
 * AnimatedCounter — counts from 0 to a number when scrolled into view.
 * Supports prefix/suffix and decimals; for range strings like "$60–80B" use AnimatedRangeLabel below.
 */
export const AnimatedCounter = ({ to = 100, duration = 1500, prefix = '', suffix = '', decimals = 0, className = '' }) => {
  const ref = useRef(null);
  const [value, setValue] = useState(0);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        obs.disconnect();
        const start = performance.now();
        const ease = (t) => 1 - Math.pow(1 - t, 3);
        const tick = (now) => {
          const p = Math.min(1, (now - start) / duration);
          setValue(Number(to) * ease(p));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.3 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [to, duration]);

  const formatted = value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return <span ref={ref} className={className}>{prefix}{formatted}{suffix}</span>;
};

/**
 * SectionEyebrow — unified eyebrow label across all pages.
 */
export const SectionEyebrow = ({ children, light = false }) => (
  <div className={`inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.2em] uppercase ${light ? 'text-[#7fd6c9]' : 'text-[#2f9e8e]'}`}>
    <span className={`w-8 h-px ${light ? 'bg-[#7fd6c9]/60' : 'bg-[#2f9e8e]/60'}`} /> {children}
  </div>
);
